import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { sdk } from '../lib/sdk';

export interface User {
  userId: string;
  role: 'SuperAdmin' | 'Admin' | 'Cashier';
  email: string;
  name: string;
  preferences?: {
    reportLanguage: 'en' | 'ur';
    fontSize: 'sm' | 'md' | 'lg';
    theme: 'light' | 'dark' | 'system';
    slipPrinter?: string;
    reportPrinter?: string;
  };
  deviceId: string;
  lastActivity: Date;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  setUser: (user: User) => void;
  refreshSession: () => Promise<void>;
  clearError: () => void;
  updateTheme: (theme: 'light' | 'dark' | 'system') => void;
  getCurrentTheme: () => 'light' | 'dark' | 'system';
  checkTokenExpiration: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          // Get device ID from Electron if available
          let deviceId = 'desktop'; // Fallback
          try {
            if (window.electronAPI?.getMachineId) {
              const machineInfo = await window.electronAPI.getMachineId();
              if (machineInfo?.deviceId) {
                deviceId = machineInfo.deviceId;
              }
            }
          } catch (error) {
            console.warn('Failed to get machine ID, using fallback:', error);
          }
          
          const response = await sdk.auth.login({ email, password, deviceId });
          
          if (response.success && response.data?.token && response.data?.user) {
            const token = response.data.token;
            const userData = response.data.user;
            
            // Store token in localStorage
            window.localStorage.setItem('hria-token', token);
            
          const user: User = {
            userId: userData.id,
            role: userData.role,
            email: userData.email,
            name: userData.fullName,
            preferences: {
              reportLanguage: 'en',
              fontSize: 'md',
              theme: 'system',
            },
            deviceId, // Use the actual device ID
            lastActivity: new Date(),
          };

          // Store session in local database (for offline access)
          try {
            if (window.electronAPI?.dbStoreSession) {
              // Calculate expiration (12:00 AM next day, Asia/Karachi)
              const now = new Date();
              const karachiTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Karachi' }));
              const tomorrow = new Date(karachiTime);
              tomorrow.setDate(tomorrow.getDate() + 1);
              tomorrow.setHours(0, 0, 0, 0);
              
              // Convert back to UTC
              const expiresAt = new Date(tomorrow.toLocaleString('en-US', { timeZone: 'UTC' }));
              
              await window.electronAPI.dbStoreSession({
                userId: userData.id,
                userEmail: userData.email,
                userRole: userData.role,
                deviceId,
                loginTimestamp: new Date().toISOString(),
                expiresAt: expiresAt.toISOString(),
                token: token // Store encrypted token
              });
              
            }
          } catch (error) {
            console.error('Error storing session:', error);
            // Don't block login if session storage fails
          }

          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
          return true;
          } else {
            set({
              isLoading: false,
              error: response.message || 'Login failed',
            });
            return false;
          }
        } catch (error: any) {
          const status: number | undefined = error?.response?.status;
          const serverMessage: string | undefined = error?.response?.data?.message;

          let errorMessage: string;
          if (!error?.response) {
            errorMessage = 'Server is unreachable. Please check your connection and try again.';
          } else if (status === 401) {
            errorMessage = 'Incorrect email or password. Please try again.';
          } else if (status === 403) {
            errorMessage = 'Your account has been disabled. Contact your administrator.';
          } else if (status === 404) {
            errorMessage = 'No account found with this email.';
          } else if (status === 429) {
            errorMessage = 'Too many login attempts. Please wait a moment and try again.';
          } else if (typeof status === 'number' && status >= 500) {
            errorMessage = 'Server is unreachable. Please check your connection and try again.';
          } else {
            errorMessage = serverMessage || 'Login failed. Please try again.';
          }

          set({
            isLoading: false,
            error: errorMessage,
          });
          return false;
        }
      },

      logout: async () => {
        // Clear session from local database
        try {
          if (window.electronAPI?.dbClearSession) {
            await window.electronAPI.dbClearSession();
          }
        } catch (error) {
          console.error('Error clearing session:', error);
        }
        
        window.localStorage.removeItem('hria-token');
        set({ 
          user: null, 
          token: null,
          isAuthenticated: false, 
          error: null
        });
      },

      setUser: (user: User) => {
        set({ user });
      },

      clearError: () => {
        set({ error: null });
      },

      updateTheme: (theme: 'light' | 'dark' | 'system') => {
        const current = get();
        if (current.user) {
          const updatedUser = {
            ...current.user,
            preferences: {
              ...current.user.preferences,
              theme
            }
          };
          set({ user: updatedUser as User });
          
          // Apply theme to document
          const root = document.documentElement;
          root.classList.remove('light', 'dark');
          
          if (theme === 'system') {
            const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
            root.classList.add(systemTheme);
            root.setAttribute('data-theme', systemTheme);
          } else {
            root.classList.add(theme);
            root.setAttribute('data-theme', theme);
          }
          
          // Save to localStorage
          window.localStorage.setItem('hria-theme', theme);
        }
      },

      getCurrentTheme: () => {
        const current = get();
        return current.user?.preferences?.theme || 'system';
      },

      refreshSession: async () => {
        const token = window.localStorage.getItem('hria-token');
        
        if (!token) {
          set({ isAuthenticated: false, isLoading: false });
          return;
        }
        
        // If we have a token but no user data, try to restore from localStorage
        const current = get();
        if (token && !current.user) {
          try {
            const response = await sdk.auth.profile();
            
            if (response.success && response.data?.user) {
              const userData = response.data.user;
              
              // Get device ID from Electron if available
              let deviceId = 'desktop'; // Fallback
              try {
                if (window.electronAPI?.getMachineId) {
                  const machineInfo = await window.electronAPI.getMachineId();
                  if (machineInfo?.deviceId) {
                    deviceId = machineInfo.deviceId;
                  }
                }
              } catch (error) {
                console.warn('Failed to get machine ID, using fallback:', error);
              }
              
              set({
                user: {
                  userId: userData.id,
                  role: userData.role,
                  email: userData.email,
                  name: userData.fullName,
                  preferences: { 
                    reportLanguage: 'en', 
                    fontSize: 'md', 
                    theme: 'system',
                    slipPrinter: '',
                    reportPrinter: ''
                  },
                  deviceId, // Use the actual device ID
                  lastActivity: new Date(),
                },
                token,
                isAuthenticated: true,
                isLoading: false,
              });
              
            } else {
            
              window.localStorage.removeItem('hria-token');
              set({ isAuthenticated: false, isLoading: false });
            }
          } catch (error: any) {
          
            window.localStorage.removeItem('hria-token');
            set({ isAuthenticated: false, isLoading: false });
          }
        } else if (token) {
          // Token exists and user data exists, check if token is expired
          
          set({ isAuthenticated: true, isLoading: false });
        } else {
          set({ isAuthenticated: false, isLoading: false });
        }
      },

      // Check if token is expired or near expiry; refresh proactively via /api/v1/auth/refresh-token
      checkTokenExpiration: () => {
        const current = get();
        if (!current.isAuthenticated || !current.token) return;

        const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

        try {
          const tokenParts = current.token.split('.');
          if (tokenParts.length !== 3) {
            get().logout();
            return;
          }
          const payload = JSON.parse(atob(tokenParts[1]));
          const currentTimeSec = Math.floor(Date.now() / 1000);
          const exp = payload.exp as number | undefined;

          if (exp && exp < currentTimeSec) {
            get().logout();
            return;
          }

          // Proactive refresh: if token expires within window, refresh and update token
          const refreshBeforeSec = REFRESH_BEFORE_EXPIRY_MS / 1000;
          if (exp && exp - currentTimeSec <= refreshBeforeSec) {
            sdk.auth
              .refresh()
              .then((res) => {
                if (res.success && res.data?.token) {
                  window.localStorage.setItem('hria-token', res.data.token);
                  set({ token: res.data.token });
                } else {
                  get().logout();
                }
              })
              .catch(() => get().logout());
            return;
          }

          // Midnight Asia/Karachi policy: logout if past midnight and token was issued before today
          const now = new Date();
          const karachiTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Karachi' }));
          const todayMidnight = new Date(karachiTime);
          todayMidnight.setHours(0, 0, 0, 0);
          if (
            now.getTime() >= todayMidnight.getTime() &&
            payload.iat != null &&
            payload.iat < Math.floor(todayMidnight.getTime() / 1000)
          ) {
            get().logout();
          }
        } catch {
          get().logout();
        }
      },
    }),
    {
      name: 'hria-auth',
      partialize: (state) => ({ 
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated
      }),
    }
  )
); 